import React from 'react';
import { motion } from 'framer-motion';
import { Terminal, Calendar, Code, Cpu, ArrowUpRight, Sparkles, Layers } from 'lucide-react';
import { Link } from 'react-router-dom';

const GenesisSection = ({ isDark, lang }) => {
  const isEs = lang === 'es';

  const timelineData = [
    {
      year: "2019",
      icon: Cpu,
      badge: isEs ? "Fase 01" : "Phase 01",
      label: isEs ? "Semillero Académico" : "Academic Lab",
      detail: isEs ? "Investigación y arquitecturas móviles experimentales" : "Research & experimental mobile architectures",
      color: "from-blue-500/20 to-blue-600/5",
      border: "border-blue-500/30",
      accent: "text-blue-500"
    },
    {
      year: "2021",
      icon: Code,
      badge: isEs ? "Fase 02" : "Phase 02",
      label: isEs ? "Evolución Nativa" : "Native Engineering",
      detail: isEs ? "Ecosistemas nativos cloud y servicios geoespaciales" : "Native cloud ecosystems & geospatial pipelines",
      color: "from-indigo-500/20 to-indigo-600/5",
      border: "border-indigo-500/30",
      accent: "text-indigo-400"
    },
    {
      year: isEs ? "Hoy" : "Now",
      icon: Terminal,
      badge: isEs ? "Fase 03" : "Phase 03",
      label: isEs ? "Plataformas Propias" : "Proprietary Platforms",
      detail: isEs ? "Kanan, Blebe, Marchantite y Niya en marcha" : "Operating Kanan, Blebe, Marchantite & Niya",
      color: "from-emerald-500/20 to-emerald-600/5",
      border: "border-emerald-500/30",
      accent: "text-emerald-400"
    }
  ];

  return (
    <section id="genesis" className="py-24 sm:py-32 px-4 sm:px-6 relative overflow-hidden">
      
      {/* Luz ambiental sutil detrás del timeline */}
      <div className={`absolute top-1/2 right-10 -translate-y-1/2 w-96 h-96 rounded-full blur-[140px] pointer-events-none opacity-20 ${
        isDark ? 'bg-blue-600' : 'bg-blue-300'
      }`} />

      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          
          {/* COLUMNA IZQUIERDA: MENSAJE ESTRATÉGICO & CTA */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-6 text-left"
          >
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-blue-500/20 bg-blue-500/10 mb-6">
              <Calendar className="w-3.5 h-3.5 text-blue-500" />
              <span className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-400">
                {isEs ? 'TRAYECTORIA // DESDE 2019' : 'TIMELINE // SINCE 2019'}
              </span>
            </div>
            
            <h2 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter leading-tight italic mb-6">
              {isEs ? (
                <>De un laboratorio a un <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-indigo-400 to-blue-300">ecosistema propio.</span></>
              ) : (
                <>From a research lab to an <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 via-indigo-400 to-blue-300">independent ecosystem.</span></>
              )}
            </h2>
            
            <p className="text-base sm:text-lg font-bold opacity-75 leading-relaxed max-w-xl mb-8">
              {isEs
                ? 'No nacimos como una agencia convencional ni vendemos plantillas genéricas. Comenzamos en aulas de ingeniería resolviendo problemas vecinales y evolucionamos construyendo plataformas móviles seguras, directas y con respaldo legal.'
                : 'We did not start as a generic agency. We emerged from engineering labs solving real community friction and evolved to operate robust, privacy-first mobile platforms.'}
            </p>

            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/nosotros"
                className="px-8 py-4 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white font-black uppercase text-xs tracking-widest transition-all shadow-xl shadow-blue-600/25 flex items-center gap-2 group active:scale-95"
              >
                <span>{isEs ? 'Conocer Nuestra Historia' : 'Read Our Full Story'}</span>
                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </div>
          </motion.div>

          {/* COLUMNA DERECHA: CONSOLA TIMELINE ESTILO VENTURE STUDIO */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-6"
          >
            <Link
              to="/nosotros"
              className={`block p-6 sm:p-10 rounded-[2.5rem] sm:rounded-[3rem] border backdrop-blur-3xl transition-all duration-500 group relative overflow-hidden ${
                isDark 
                  ? 'bg-slate-900/60 border-white/10 hover:border-blue-500/40 hover:shadow-2xl hover:shadow-blue-500/10' 
                  : 'bg-white border-slate-200 hover:border-blue-500/40 shadow-xl shadow-blue-500/5'
              }`}
            >
              {/* Header de consola */}
              <div className="flex items-center justify-between pb-6 border-b border-current/10 mb-8">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-xl bg-blue-500/10 text-blue-500">
                    <Layers size={16} />
                  </div>
                  <div>
                    <span className="text-[10px] font-black uppercase tracking-[0.25em] block opacity-40">
                      ROADMAP LOG
                    </span>
                    <span className="text-xs font-black uppercase tracking-wider">
                      {isEs ? 'Evolución de Arquitectura' : 'Architectural Evolution'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-1.5 text-xs font-black uppercase tracking-widest text-blue-500 group-hover:translate-x-1 transition-transform">
                  <span>{isEs ? 'Ver Más' : 'Explore'}</span>
                  <ArrowUpRight size={14} />
                </div>
              </div>

              {/* Items del Timeline con Línea Conectora */}
              <div className="relative pl-6 space-y-8 before:absolute before:left-[11px] before:top-3 before:bottom-3 before:w-[2px] before:bg-gradient-to-b before:from-blue-500 before:via-indigo-500 before:to-emerald-500">
                {timelineData.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={index}
                      whileHover={{ x: 6 }}
                      transition={{ type: "spring", stiffness: 300, damping: 20 }}
                      className="relative flex items-start gap-4 text-left"
                    >
                      {/* Nodo sobre la línea */}
                      <span className={`absolute -left-[30px] top-1.5 w-4 h-4 rounded-full border-2 ${
                        isDark ? 'bg-slate-900 border-blue-500' : 'bg-white border-blue-600'
                      } group-hover:scale-125 transition-transform`} />

                      {/* Contenido */}
                      <div className={`w-full p-4 sm:p-5 rounded-2xl border transition-all bg-gradient-to-br ${item.color} ${
                        isDark ? 'border-white/5 group-hover:border-white/20' : 'border-slate-200 group-hover:border-blue-300'
                      }`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`text-xl font-black ${item.accent}`}>
                              {item.year}
                            </span>
                            <span className="text-xs font-black uppercase tracking-wide">
                              {item.label}
                            </span>
                          </div>
                          <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded-md bg-current/5 border border-current/10 opacity-70">
                            {item.badge}
                          </span>
                        </div>
                        <p className="text-xs font-bold opacity-60 leading-relaxed">
                          {item.detail}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>

              {/* Footer de tarjeta */}
              <div className="mt-8 pt-6 border-t border-current/10 flex items-center justify-between text-[10px] font-black uppercase tracking-wider opacity-50">
                <span className="flex items-center gap-1.5">
                  <Sparkles size={12} className="text-blue-500" />
                  {isEs ? 'Innovación Continua' : 'Continuous R&D'}
                </span>
                <span>TDSMX STUDIO</span>
              </div>
            </Link>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default GenesisSection;